import { useEffect, type ReactNode } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useBranding } from "@/hooks/useBranding";
import { stopReplayOnPublicReport } from "@/sentry";
import type { PublicRiskScanView } from "@shared/risk-scan-public";
import { presentAuditReport } from "@shared/site-audit-report-view";
import { SecurityCoverage } from "@/components/site-audit/report/security-coverage";

export default function RiskScanPage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const { brandName } = useBranding();
  const query = useQuery<PublicRiskScanView>({
    queryKey: ["/api/site-audit/public", token],
    enabled: token.length > 0,
    retry: false,
    queryFn: async () => {
      const response = await fetch(`/api/site-audit/public/${encodeURIComponent(token)}`);
      if (!response.ok) throw new Error("Could not load this scan");
      return response.json() as Promise<PublicRiskScanView>;
    },
  });

  const brand = query.data?.brand;
  useEffect(() => {
    const name = brand?.name || brandName || "HeyHiHello";
    document.title = `${name} · Platform Risk Scan`;
  }, [brand?.name, brandName]);

  useEffect(() => {
    stopReplayOnPublicReport();
  }, []);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  if (query.isLoading) {
    return (
      <Shell brandName={brandName || "HeyHiHello"} logoUrl={null} color={null}>
        <p className="text-sm text-muted-foreground">Loading the scan…</p>
      </Shell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Shell brandName={brandName || "HeyHiHello"} logoUrl={null} color={null}>
        <h1 className="text-2xl font-semibold tracking-tight">Platform Risk Scan</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          This results link could not be loaded. Try it again in a moment.
        </p>
      </Shell>
    );
  }

  const view = query.data;
  return (
    <Shell brandName={view.brand.name} logoUrl={view.brand.logoUrl} color={view.brand.primaryColorHsl}>
      <p className="text-sm font-medium" style={{ color: `hsl(${view.brand.primaryColorHsl})` }}>
        Platform Risk Scan
      </p>
      {view.state === "missing" && (
        <>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">This link is not valid</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The address does not match a scan. Check the link you were sent.
          </p>
        </>
      )}
      {view.state === "expired" && (
        <>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">This results link has expired</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            It stopped working on {formatWhen(view.expiresAt)}. Ask us for a new scan.
          </p>
        </>
      )}
      {view.state === "ready" && <ReadyScan view={view} />}
    </Shell>
  );
}

function ReadyScan({ view }: { view: Extract<PublicRiskScanView, { state: "ready" }> }) {
  const security = presentAuditReport({
    domain: view.domain,
    wordpress: view.wordpress,
    mode: "staff",
    jobComplete: view.status === "complete" || view.status === "failed",
  }).security;
  return (
    <>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">{view.domain}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{view.statusLabel}</p>
      <div className="mt-8">
        <SecurityCoverage security={security} showCve={false} />
      </div>
      <p className="mt-8 text-xs text-muted-foreground">
        This link works until {formatWhen(view.expiresAt)}.
      </p>
    </>
  );
}

function Shell({
  brandName,
  logoUrl,
  color,
  children,
}: {
  brandName: string;
  logoUrl: string | null;
  color: string | null;
  children: ReactNode;
}) {
  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-10 flex items-center gap-4 border-b pb-6">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-10 w-auto max-w-[180px] object-contain" />
          ) : (
            <span
              className="text-lg font-semibold tracking-tight"
              style={color ? { color: `hsl(${color})` } : undefined}
            >
              {brandName}
            </span>
          )}
          {logoUrl && <span className="text-sm text-muted-foreground">{brandName}</span>}
        </header>
        {children}
      </div>
    </main>
  );
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "the expiry date";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}
